import axios, { AxiosResponse } from 'axios';
import {
  ApiResponse,
  BatchJob,
  BatchExecutionHistory,
  BatchExecutionStatus,
  BatchItemExecutionHistory,
  BatchStatistics,
  BatchJobType,
  BatchJobConfig,
  BatchJobConfigFormData,
  ExecuteManuallyResponse
} from '../types/batch';

import { API_BASE_URL as BASE } from './apiConfig';
const API_BASE_URL = `${BASE}/api/admin/batch`;

// Axios 인스턴스 생성
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 요청 인터셉터: 토큰 자동 추가
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 401 에러 처리
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 토큰 만료 시 로그인 페이지로 리다이렉트
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const batchApi = {
  /**
   * 1. 전체 배치 작업 목록 및 최근 실행 상태 조회
   */
  getAllBatchJobs: (): Promise<AxiosResponse<ApiResponse<BatchJob[]>>> => {
    return axiosInstance.get('/jobs');
  },

  /**
   * 2. 특정 배치 작업의 실행 이력 조회
   */
  getBatchHistory: (
    jobType: BatchJobType, 
    page: number = 0, 
    size: number = 20
  ): Promise<AxiosResponse<ApiResponse<BatchExecutionHistory[]>>> => {
    return axiosInstance.get(`/jobs/${jobType}/history`, {
      params: { page, size }
    });
  },

  /**
   * 3. 전체 배치 실행 이력 조회
   */
  getAllBatchHistory: (
    page: number = 0, 
    size: number = 20
  ): Promise<AxiosResponse<ApiResponse<BatchExecutionHistory[]>>> => {
    return axiosInstance.get('/history', {
      params: { page, size }
    });
  },

  /**
   * 4. 실패한 배치 실행 이력 조회
   */
  getFailedBatchHistory: (
    page: number = 0, 
    size: number = 20
  ): Promise<AxiosResponse<ApiResponse<BatchExecutionHistory[]>>> => {
    return axiosInstance.get('/history/failed', {
      params: { page, size }
    });
  },

  /**
   * 4-1. 배치 실행 이력 검색 (배치종류 + 상태 + 기간 조합, 전부 선택)
   * 실행 이력 목록의 탭(전체/실패/실행중)과 배치종류/기간 필터를 함께 적용한다.
   */
  searchBatchHistory: (
    params: {
      jobType?: BatchJobType;
      status?: BatchExecutionStatus;
      startDate?: string;
      endDate?: string;
      page?: number;
      size?: number;
    }
  ): Promise<AxiosResponse<ApiResponse<BatchExecutionHistory[]>>> => {
    return axiosInstance.get('/history/search', { params });
  },

  /**
   * 5. 특정 배치 실행 이력 상세 조회
   */
  getBatchHistoryDetail: (
    historyId: number
  ): Promise<AxiosResponse<ApiResponse<BatchExecutionHistory>>> => {
    return axiosInstance.get(`/history/${historyId}`);
  },

  /**
   * 5-1. 특정 배치 실행에 속한 개별 건(대상 계정) 실행 이력 조회 - before/after 상태 확인용
   */
  getBatchHistoryItems: (
    historyId: number,
    page: number = 0,
    size: number = 20
  ): Promise<AxiosResponse<ApiResponse<BatchItemExecutionHistory[]>>> => {
    return axiosInstance.get(`/history/${historyId}/items`, {
      params: { page, size }
    });
  },

  /**
   * 6. 배치 작업 수동 실행
   */
  executeManually: (
    jobType: BatchJobType
  ): Promise<AxiosResponse<ExecuteManuallyResponse>> => {
    return axiosInstance.post(`/jobs/${jobType}/execute`);
  },

  /**
   * 7. 배치 통계 조회
   */
  getBatchStatistics: (
    startDate?: string, 
    endDate?: string
  ): Promise<AxiosResponse<ApiResponse<BatchStatistics>>> => {
    return axiosInstance.get('/statistics', {
      params: { startDate, endDate }
    });
  },

  /**
   * 8. 실행 중인 배치 조회
   */
  getRunningBatchJobs: (): Promise<AxiosResponse<ApiResponse<BatchExecutionHistory[]>>> => {
    return axiosInstance.get('/running');
  },

  /**
   * 9. 오래된 RUNNING 배치 일괄 정리
   */
  cleanupStuckBatches: (): Promise<AxiosResponse<ApiResponse<{ cleanedCount: number }>>> => {
    return axiosInstance.post('/cleanup-stuck');
  },

  /**
   * 10. 특정 배치 강제 종료
   */
  forceFailBatch: (
    historyId: number,
    reason?: string
  ): Promise<AxiosResponse<ApiResponse<null>>> => {
    return axiosInstance.post(`/history/${historyId}/force-fail`, {
      reason: reason || '관리자에 의한 강제 종료'
    });
  },

  // ========== 배치 설정 관리 API (신규) ==========
  
  /**
   * 11. 전체 배치 설정 조회
   */
  getAllBatchConfigs: (): Promise<AxiosResponse<ApiResponse<BatchJobConfig[]>>> => {
    return axiosInstance.get('/config');
  },

  /**
   * 12. 활성화된 배치 설정만 조회
   */
  getActiveBatchConfigs: (): Promise<AxiosResponse<ApiResponse<BatchJobConfig[]>>> => {
    return axiosInstance.get('/config/active');
  },

  /**
   * 13. 특정 배치 설정 조회
   */
  getBatchConfig: (id: number): Promise<AxiosResponse<ApiResponse<BatchJobConfig>>> => {
    return axiosInstance.get(`/config/${id}`);
  },

  /**
   * 14. 배치 설정 추가
   */
  createBatchConfig: (
    data: BatchJobConfigFormData
  ): Promise<AxiosResponse<ApiResponse<BatchJobConfig>>> => {
    return axiosInstance.post('/config', data);
  },

  /**
   * 15. 배치 설정 수정
   */
  updateBatchConfig: (
    id: number,
    data: BatchJobConfigFormData
  ): Promise<AxiosResponse<ApiResponse<BatchJobConfig>>> => {
    return axiosInstance.put(`/config/${id}`, data);
  },

  /**
   * 16. 배치 설정 삭제
   */
  deleteBatchConfig: (id: number): Promise<AxiosResponse<ApiResponse<null>>> => {
    return axiosInstance.delete(`/config/${id}`);
  },

  /**
   * 17. 배치 활성화/비활성화
   */
  toggleBatchActive: (
    id: number,
    isActive: boolean
  ): Promise<AxiosResponse<ApiResponse<null>>> => {
    return axiosInstance.patch(`/config/${id}/active?isActive=${isActive}`);
  }
};
